// One caster as a plate: its symbol, its name, the energy of its
// side as pips, and its four slots. The symbol and the slots have the colors
// of the caster, through `data-caster`. It holds no state of the game. The owner draws it.
// Three places use it, so a caster looks the same in each:
//   desk   the caster of the player, in the team sheet of the player. A click on a slot casts.
//   score  the caster of the other side, small, in the team sheet of that side.
//   pick   a caster in the caster select, before a match. No side, no energy.

import type { CasterId } from '../content/casters';
import type { Side } from '../engine/types';
import type { Slot } from '../game/casters';
import { el } from '../lib/dom';
import { createCasterPawn, createCasterPawnSmall } from './CasterMark';
import { createEnergyPips } from './EnergyPips';
import { createSlotFace } from './SlotFace';

export type CasterPlateVariant = 'desk' | 'score' | 'pick';

export type CasterPlateView = {
  id: CasterId;
  name: string;
  slots: readonly Slot[];
  /** Null where the caster has no energy yet: the caster select. The pips hide. */
  energy: number | null;
  /**
   * False for a plate that shows no pips of its own. In a match a side has one
   * pool of energy for its casters, and `CasterRow.ts` shows it over their
   * plates. The faces still fill by the energy.
   */
  pips: boolean;
  /** For each slot, whether a click casts from it now. */
  open: readonly boolean[];
  /** Null for a plate that is only read. */
  onCast: ((index: number) => void) | null;
  /** Fills the pips with no pop and no tick: a new match, or an earlier step. */
  quiet: boolean;
};

export type CasterPlate = {
  root: HTMLElement;
  draw: (view: CasterPlateView) => void;
  /** The box on the screen of the slot at `index`. Null when the plate is not on the screen. */
  slotBox: (index: number) => DOMRect | null;
};

/** `side` is null for a caster that has no side yet. */
export const createCasterPlate = (side: Side | null, variant: CasterPlateVariant): CasterPlate => {
  const name = el('span', { className: 'caster__name' });
  const pips = side === null ? null : createEnergyPips(side);
  const faces = el('ol', { className: 'caster__slots' });
  const who = el(variant === 'pick' ? 'span' : 'h3', { className: 'caster__who' }, [name]);
  const root = el(
    variant === 'pick' ? 'span' : 'section',
    { className: `caster caster--${variant}${side ? ` caster--${side}` : ''}` },
    [who, pips?.root, faces],
  );
  let drawn = '';

  const draw = (view: CasterPlateView): void => {
    name.textContent = view.name;
    if (root.dataset.caster !== view.id) {
      root.dataset.caster = view.id;
      // A plate in a match has the small pawn of the caster, cropped. A pick plate has it in a square.
      who.replaceChildren(
        variant === 'pick' ? createCasterPawn(view.id) : createCasterPawnSmall(view.id),
        name,
      );
    }
    if (pips) {
      pips.root.hidden = view.energy === null || !view.pips;
      if (view.energy !== null && view.pips) pips.draw(view.energy, view.quiet);
    }
    // The faces change when a slot does, when the energy does, or when a slot opens or closes.
    const signature = JSON.stringify([view.name, view.slots, view.energy, view.open]);
    if (signature === drawn) return;
    drawn = signature;
    const { onCast } = view;
    faces.replaceChildren(
      ...view.slots.map((slot, index) =>
        createSlotFace({
          slot,
          energy: view.energy,
          open: view.open[index] === true,
          onCast: onCast === null ? null : () => onCast(index),
        }),
      ),
    );
  };

  const slotBox = (index: number): DOMRect | null => {
    const box = faces.children[index]?.getBoundingClientRect();
    return box && box.width > 0 ? box : null;
  };

  return { root, draw, slotBox };
};
